import React from 'react';
import {
    Document,
    Page,
    Text,
    View,
    StyleSheet
} from '@react-pdf/renderer';
import PDFBoxPlot from './pdf-components/PDFBoxPlot';
import PDFOrganCircle from './pdf-components/PDFOrganCircle';
import { calculateDose, formatFrequency } from '../../util/RecUtil';
import { getRangeStatus, getStatusLabel, formatDate } from '../../util/GenericUtil';
import { GREEN_HEADER, GREEN_HEADER_LIGHT, GROOVE_BORDER, ROW_NAME_BG } from '../../util/Colors';
import {
    FOCUS_LOOKUP,
    CATEGORY_DEFINITIONS,
    ASSESSMENT_SHORT_LABELS,
    filterAssessmentsBySex,
} from '../../data/categoryDefinitions';
import supplementsCatalog from '../../../lambda/health-recommendations/supplements.json';

// Build a lookup: product name -> dose fields from the catalog
const doseLookup = {};
for (const [name, config] of Object.entries(supplementsCatalog)) {
    if (name === '_metadata') continue;
    doseLookup[name] = {
        min_daily_dose: config.min_daily_dose,
        max_support_daily_dose: config.max_support_daily_dose,
        max_therapeutic_daily_dose: config.max_therapeutic_daily_dose,
        max_single_dose: config.max_single_dose,
        unit: config.unit,
        freq_unit: config.freq_unit,
    };
}

/**
 * Styles modeled after the blood_trending_cloud visual_report.css:
 * - Green header (rgb(135, 197, 125) gradient approximated as solid)
 * - Helvetica font at 90%
 * - Row names with subtle background
 * - Groove-style bottom borders on cells
 * - Boxplot column with light background
 */
const greenHeader = GREEN_HEADER;
const greenHeaderLight = GREEN_HEADER_LIGHT;
const grooveBorder = GROOVE_BORDER;
const rowNameBg = ROW_NAME_BG;

const styles = StyleSheet.create({
    page: {
        padding: 35,
        fontSize: 9,
        fontFamily: 'Helvetica'
    },
    // Report header
    header: {
        marginBottom: 20,
        paddingBottom: 10,
        borderBottomWidth: 2,
        borderBottomColor: greenHeader
    },
    title: {
        fontSize: 22,
        fontWeight: 'bold',
        fontFamily: 'Helvetica-Bold',
        color: '#1f2937',
        marginBottom: 4
    },
    subtitle: {
        fontSize: 10,
        color: '#6b7280',
        fontFamily: 'Helvetica'
    },
    // Sections
    section: {
        marginBottom: 18
    },
    sectionTitle: {
        fontSize: 13,
        fontWeight: 'bold',
        fontFamily: 'Helvetica-Bold',
        color: '#1f2937',
        marginBottom: 10,
        paddingBottom: 4,
        paddingLeft: 6,
        borderBottomWidth: 2,
        borderBottomColor: greenHeader,
        backgroundColor: greenHeaderLight,
        paddingVertical: 6,
        borderRadius: 3
    },
    // Organ scores grid
    organGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'flex-start',
        gap: 6,
        paddingHorizontal: 4
    },
    organItem: {
        width: 100,
        alignItems: 'center',
        marginBottom: 6
    },
    // Test results table
    table: {
        width: '100%',
        borderRadius: 6,
        overflow: 'hidden'
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: greenHeader,
        paddingVertical: 8,
        paddingHorizontal: 6
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: grooveBorder,
        paddingVertical: 5,
        paddingHorizontal: 6,
        alignItems: 'center',
        minHeight: 34
    },
    tableRowAlt: {
        backgroundColor: '#f9fafb'
    },
    colName: {
        width: '24%',
        paddingRight: 6,
        backgroundColor: rowNameBg,
        marginRight: 4,
        paddingVertical: 4,
        paddingLeft: 4,
        borderRadius: 2
    },
    colResult: {
        width: '12%',
        textAlign: 'center'
    },
    colRange: {
        width: '14%',
        textAlign: 'center'
    },
    colStatus: {
        width: '13%',
        textAlign: 'center'
    },
    colChart: {
        width: '37%',
        paddingLeft: 6
    },
    headerText: {
        fontWeight: 'bold',
        fontFamily: 'Helvetica-Bold',
        fontSize: 9,
        color: '#000000'
    },
    cellText: {
        fontSize: 8,
        color: '#1f2937',
        fontFamily: 'Helvetica'
    },
    nameText: {
        fontSize: 8,
        color: '#1f2937',
        fontFamily: 'Helvetica-Bold'
    },
    // Status badges
    statusUnranged: {
        backgroundColor: '#6b728026',
        color: '#374151',
        paddingVertical: 2,
        paddingHorizontal: 5,
        borderRadius: 3,
        fontSize: 7,
        fontFamily: 'Helvetica-Bold',
        textAlign: 'center'
    },
    statusOptimal: {
        backgroundColor: '#49C13340',
        color: '#166534',
        paddingVertical: 2,
        paddingHorizontal: 5,
        borderRadius: 3,
        fontSize: 7,
        fontFamily: 'Helvetica-Bold',
        textAlign: 'center'
    },
    statusElevated: {
        backgroundColor: '#FCFAB1',
        color: '#854d0e',
        paddingVertical: 2,
        paddingHorizontal: 5,
        borderRadius: 3,
        fontSize: 7,
        fontFamily: 'Helvetica-Bold',
        textAlign: 'center'
    },
    statusDiminished: {
        backgroundColor: '#FCFAB1',
        color: '#854d0e',
        paddingVertical: 2,
        paddingHorizontal: 5,
        borderRadius: 3,
        fontSize: 7,
        fontFamily: 'Helvetica-Bold',
        textAlign: 'center'
    },
    statusHigh: {
        backgroundColor: '#ff474c40',
        color: '#991b1b',
        paddingVertical: 2,
        paddingHorizontal: 5,
        borderRadius: 3,
        fontSize: 7,
        fontFamily: 'Helvetica-Bold',
        textAlign: 'center'
    },
    statusLow: {
        backgroundColor: '#ff474c40',
        color: '#991b1b',
        paddingVertical: 2,
        paddingHorizontal: 5,
        borderRadius: 3,
        fontSize: 7,
        fontFamily: 'Helvetica-Bold',
        textAlign: 'center'
    },
    // Recommendations
    recCard: {
        marginBottom: 10,
        padding: 10,
        backgroundColor: '#f9fafb',
        borderRadius: 4,
        borderLeftWidth: 4,
        borderLeftColor: greenHeader
    },
    recHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 6
    },
    recName: {
        fontSize: 10,
        fontWeight: 'bold',
        fontFamily: 'Helvetica-Bold',
        color: '#1f2937'
    },
    recCategory: {
        fontSize: 8,
        color: '#374151',
        backgroundColor: '#e5e7eb',
        paddingVertical: 2,
        paddingHorizontal: 6,
        borderRadius: 3,
        fontFamily: 'Helvetica'
    },
    recDetails: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
        marginBottom: 6
    },
    recDetail: {
        flexDirection: 'row',
        alignItems: 'center'
    },
    recDetailLabel: {
        fontSize: 8,
        color: '#6b7280',
        marginRight: 3,
        fontFamily: 'Helvetica'
    },
    recDetailValue: {
        fontSize: 8,
        color: '#1f2937',
        fontFamily: 'Helvetica-Bold'
    },
    recMarkers: {
        marginTop: 4
    },
    recMarkersLabel: {
        fontSize: 7,
        color: '#6b7280',
        marginBottom: 2,
        fontFamily: 'Helvetica'
    },
    recMarkersList: {
        fontSize: 7,
        color: '#4b5563',
        fontFamily: 'Helvetica'
    },
    // Weight/priority bar
    weightBarContainer: {
        width: 50,
        height: 5,
        backgroundColor: '#e5e7eb',
        borderRadius: 3,
        marginTop: 2
    },
    weightBar: {
        height: 5,
        backgroundColor: greenHeader,
        borderRadius: 3
    },
    // Focus areas + assessment summary
    focusCategory: {
        marginBottom: 12,
        paddingLeft: 8,
        borderLeftWidth: 3,
        borderLeftColor: greenHeader,
    },
    focusCategoryName: {
        fontSize: 11,
        fontFamily: 'Helvetica-Bold',
        color: '#1f2937',
        marginBottom: 4,
    },
    focusItem: {
        marginBottom: 4,
        paddingLeft: 6,
    },
    focusItemName: {
        fontSize: 9,
        fontFamily: 'Helvetica-Bold',
        color: '#1f2937',
    },
    focusItemDesc: {
        fontSize: 8,
        color: '#4b5563',
        fontFamily: 'Helvetica',
    },
    assessmentRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 4,
        paddingHorizontal: 6,
        borderBottomWidth: 1,
        borderBottomColor: grooveBorder,
    },
    assessmentName: {
        flex: 1,
        fontSize: 9,
        color: '#1f2937',
        fontFamily: 'Helvetica-Bold',
    },
    assessmentScore: {
        width: 70,
        textAlign: 'right',
        fontSize: 9,
        fontFamily: 'Helvetica-Bold',
        color: '#1f2937',
    },
    assessmentDate: {
        width: 110,
        textAlign: 'right',
        fontSize: 8,
        color: '#6b7280',
        fontFamily: 'Helvetica',
    },
    assessmentEmpty: {
        fontSize: 9,
        color: '#6b7280',
        fontStyle: 'italic',
        fontFamily: 'Helvetica',
        paddingHorizontal: 6,
        paddingVertical: 4,
    },
    // Footer
    pageNumber: {
        position: 'absolute',
        bottom: 25,
        right: 35,
        fontSize: 8,
        color: '#9ca3af',
        fontFamily: 'Helvetica'
    },
    footer: {
        position: 'absolute',
        bottom: 25,
        left: 35,
        fontSize: 7,
        color: '#b0b0b0',
        fontFamily: 'Helvetica'
    }
});

// Helper to get status style
const getStatusStyle = (status) => {
    const statusMap = {
        optimal: styles.statusOptimal,
        elevated: styles.statusElevated,
        diminished: styles.statusDiminished,
        high: styles.statusHigh,
        low: styles.statusLow,
        unranged: styles.statusUnranged
    };
    return statusMap[status] || styles.statusOptimal;
};

// getRangeStatus and formatDate imported from GenericUtil

// Group focuses by category and collect their (sex-filtered) assessment ids.
// Returns [{ categoryId, categoryName, focuses: [{id, name, desc}], assessmentIds: [...] }, ...]
function groupFocusesByCategory(focuses, sex) {
    if (!focuses || focuses.length === 0) return [];
    const grouped = {};
    for (const fId of focuses) {
        const focus = FOCUS_LOOKUP[fId];
        if (!focus) continue;
        const cId = focus.categoryId;
        if (!grouped[cId]) {
            grouped[cId] = {
                categoryId: cId,
                categoryName: CATEGORY_DEFINITIONS[cId]?.display_name || cId,
                focuses: [],
                assessmentIdSet: new Set(),
            };
        }
        grouped[cId].focuses.push({
            id: fId,
            name: focus.display_name,
            desc: focus.description,
        });
        for (const aid of focus.assessment_ids) {
            grouped[cId].assessmentIdSet.add(aid);
        }
    }
    return Object.values(grouped).map(g => ({
        ...g,
        assessmentIds: filterAssessmentsBySex([...g.assessmentIdSet], sex),
    }));
}

/**
 * Main PDF Document for Health Report
 * Styled after blood_trending_cloud visual reports.
 */
function HealthReportPDF({
    testDate,
    patientInfo,
    systemScores,
    tests,
    recommendations,
    recommendationMode,
    focuses,
    assessmentSummary,
    sex,
}) {
    const focusGroups = groupFocusesByCategory(focuses, sex);
    const hasAnyAssessments = focusGroups.some(g =>
        g.assessmentIds.some(aid => assessmentSummary?.assessments?.[aid])
    );
    const showFocusPage = focusGroups.length > 0 || hasAnyAssessments;
    const generatedDate = new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });

    return (
        <Document>
            {/* Page 1: Header + Organ Scores + Test Results */}
            <Page size="A4" style={styles.page}>
                {/* Header */}
                <View style={styles.header}>
                    <Text style={styles.title}>Health Report</Text>
                    <Text style={styles.subtitle}>
                        Test Date: {formatDate(testDate)} | Generated: {generatedDate}
                    </Text>
                </View>

                {/* Organ System Scores */}
                {systemScores && systemScores.length > 0 && (
                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>Organ System Health</Text>
                        <View style={styles.organGrid}>
                            {systemScores.map((system, index) => (
                                <View key={index} style={styles.organItem}>
                                    <PDFOrganCircle
                                        displayName={system.displayName}
                                        score={system.score}
                                        confidence={system.confidence}
                                        color={system.color}
                                        size={90}
                                    />
                                </View>
                            ))}
                        </View>
                    </View>
                )}

                {/* Test Results Table */}
                {tests && tests.length > 0 && (
                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>Test Results</Text>
                        <View style={styles.table}>
                            {/* Table Header */}
                            <View style={styles.tableHeader}>
                                <View style={{ width: '24%', paddingRight: 6 }}>
                                    <Text style={styles.headerText}>Test</Text>
                                </View>
                                <View style={{ width: '12%', textAlign: 'center' }}>
                                    <Text style={styles.headerText}>Result</Text>
                                </View>
                                <View style={{ width: '14%', textAlign: 'center' }}>
                                    <Text style={styles.headerText}>Lab Range</Text>
                                </View>
                                <View style={{ width: '13%', textAlign: 'center' }}>
                                    <Text style={styles.headerText}>Status</Text>
                                </View>
                                <View style={{ width: '37%', paddingLeft: 6 }}>
                                    <Text style={styles.headerText}>Range Plot</Text>
                                </View>
                            </View>

                            {/* Table Rows */}
                            {tests.map((test, index) => {
                                const status = getRangeStatus(
                                    test.result,
                                    test.lab_low,
                                    test.lab_high,
                                    test.fx_low,
                                    test.fx_high
                                );
                                return (
                                    <View
                                        key={index}
                                        style={[
                                            styles.tableRow,
                                            index % 2 === 1 ? styles.tableRowAlt : {}
                                        ]}
                                        wrap={false}
                                    >
                                        <View style={styles.colName}>
                                            <Text style={styles.nameText}>
                                                {test.test_name || test.alias}
                                            </Text>
                                        </View>
                                        <View style={styles.colResult}>
                                            <Text style={styles.cellText}>
                                                {test.result} {test.units}
                                            </Text>
                                        </View>
                                        <View style={styles.colRange}>
                                            <Text style={styles.cellText}>
                                                {test.lab_low} - {test.lab_high}
                                            </Text>
                                        </View>
                                        <View style={styles.colStatus}>
                                            <Text style={getStatusStyle(status)}>
                                                {getStatusLabel(status)}
                                            </Text>
                                        </View>
                                        <View style={styles.colChart}>
                                            <PDFBoxPlot
                                                llow={test.lab_low}
                                                lhigh={test.lab_high}
                                                fxlow={test.fx_low}
                                                fxhigh={test.fx_high}
                                                result={test.result}
                                                width={145}
                                                height={24}
                                            />
                                        </View>
                                    </View>
                                );
                            })}
                        </View>
                    </View>
                )}

                <Text style={styles.footer} fixed>PrimeMaxing Health Report</Text>
                <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => (
                    `${pageNumber} / ${totalPages}`
                )} fixed />
            </Page>

            {/* Page 2: Focus Areas + Assessment Summary */}
            {showFocusPage && (
                <Page size="A4" style={styles.page}>
                    {focusGroups.length > 0 && (
                        <View style={styles.section}>
                            <Text style={styles.sectionTitle}>Your Health Focus</Text>
                            {focusGroups.map(group => (
                                <View key={group.categoryId} style={styles.focusCategory}>
                                    <Text style={styles.focusCategoryName}>{group.categoryName}</Text>
                                    {group.focuses.map(f => (
                                        <View key={f.id} style={styles.focusItem}>
                                            <Text style={styles.focusItemName}>{f.name}</Text>
                                            {f.desc && (
                                                <Text style={styles.focusItemDesc}>{f.desc}</Text>
                                            )}
                                        </View>
                                    ))}
                                </View>
                            ))}
                        </View>
                    )}

                    {focusGroups.length > 0 && (
                        <View style={styles.section}>
                            <Text style={styles.sectionTitle}>Health Assessment Summary</Text>
                            {focusGroups.map(group => {
                                const completed = group.assessmentIds
                                    .map(aid => ({ aid, entry: assessmentSummary?.assessments?.[aid] }))
                                    .filter(x => x.entry && x.entry.total_pct != null);
                                return (
                                    <View key={group.categoryId} style={styles.focusCategory} wrap={false}>
                                        <Text style={styles.focusCategoryName}>{group.categoryName}</Text>
                                        {completed.length === 0 ? (
                                            <Text style={styles.assessmentEmpty}>
                                                No assessments completed yet.
                                            </Text>
                                        ) : (
                                            completed.map(({ aid, entry }) => {
                                                const health = Math.round(Math.max(0, Math.min(100, 100 - entry.total_pct)));
                                                const dateStr = entry.completed_at
                                                    ? new Date(entry.completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                                    : '';
                                                return (
                                                    <View key={aid} style={styles.assessmentRow}>
                                                        <Text style={styles.assessmentName}>
                                                            {ASSESSMENT_SHORT_LABELS[aid] || aid}
                                                        </Text>
                                                        <Text style={styles.assessmentScore}>{health}% health</Text>
                                                        <Text style={styles.assessmentDate}>{dateStr}</Text>
                                                    </View>
                                                );
                                            })
                                        )}
                                    </View>
                                );
                            })}
                        </View>
                    )}

                    <Text style={styles.footer} fixed>PrimeMaxing Health Report</Text>
                    <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => (
                        `${pageNumber} / ${totalPages}`
                    )} fixed />
                </Page>
            )}

            {/* Page 3: Recommendations */}
            {recommendations && recommendations.length > 0 && (
                <Page size="A4" style={styles.page}>
                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>
                            Supplement Recommendations ({recommendationMode === 'therapeutic' ? 'Therapeutic' : 'Support'} Mode)
                        </Text>

                        {recommendations.map((rec, index) => {
                            // Resolve product_details: prefer one with dose fields, fall back to nested products
                            const rawDetails = rec.product_details?.min_daily_dose != null
                                ? rec.product_details
                                : rec.products?.[0]?.product_details || rec.product_details || {};
                            const productName = rawDetails.product_name || rec.name || rec.supplement
                                || rec.products?.[0]?.name || 'Unknown';
                            // If dose fields still missing, backfill from supplements.json catalog
                            const catalogDose = doseLookup[productName] || {};
                            const details = rawDetails.min_daily_dose != null
                                ? rawDetails
                                : { ...catalogDose, ...rawDetails };
                            const category = rec.category
                                || rec.fulfills_categories?.[0]?.category
                                || details.primary_category?.[0]
                                || '';
                            const doseCalc = rec.doseOverride || calculateDose(
                                details,
                                rec.total_weight,
                                recommendationMode
                            );
                            // contributing_markers can be strings or {marker, contribution} objects
                            const markers = (rec.contributing_markers || [])
                                .map(m => (typeof m === 'string' ? m : m?.marker))
                                .filter(Boolean);

                            return (
                                <View key={index} style={styles.recCard} wrap={false}>
                                    <View style={styles.recHeader}>
                                        <Text style={styles.recName}>
                                            {index + 1}. {productName}
                                        </Text>
                                        <Text style={styles.recCategory}>
                                            {category.replace(/_/g, ' ')}
                                        </Text>
                                    </View>

                                    <View style={styles.recDetails}>
                                        {doseCalc && (
                                            <>
                                                <View style={styles.recDetail}>
                                                    <Text style={styles.recDetailLabel}>Dose:</Text>
                                                    <Text style={styles.recDetailValue}>
                                                        {doseCalc.dose ?? doseCalc.dailyTotal} {details.unit || 'units'}
                                                    </Text>
                                                </View>
                                                <View style={styles.recDetail}>
                                                    <Text style={styles.recDetailLabel}>Frequency:</Text>
                                                    <Text style={styles.recDetailValue}>
                                                        {formatFrequency(doseCalc.frequency, details.freq_unit)}
                                                    </Text>
                                                </View>
                                            </>
                                        )}
                                        <View style={styles.recDetail}>
                                            <Text style={styles.recDetailLabel}>Timing:</Text>
                                            <Text style={styles.recDetailValue}>
                                                {(rec.doseOverride?.emptyStomach ?? details.empty_stomach) ? 'Empty stomach' : 'With food'}
                                            </Text>
                                        </View>
                                    </View>

                                    {(rec.doseOverride?.instructions || details.special_instructions) && (
                                        <View style={styles.recMarkers}>
                                            <Text style={styles.recMarkersLabel}>Instructions:</Text>
                                            <Text style={styles.recMarkersList}>
                                                {rec.doseOverride?.instructions || details.special_instructions}
                                            </Text>
                                        </View>
                                    )}

                                    {markers.length > 0 && (
                                        <View style={styles.recMarkers}>
                                            <Text style={styles.recMarkersLabel}>Based on:</Text>
                                            <Text style={styles.recMarkersList}>
                                                {markers.join(', ')}
                                            </Text>
                                        </View>
                                    )}
                                </View>
                            );
                        })}
                    </View>

                    <Text style={styles.footer} fixed>PrimeMaxing Health Report</Text>
                    <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => (
                        `${pageNumber} / ${totalPages}`
                    )} fixed />
                </Page>
            )}
        </Document>
    );
}

export default HealthReportPDF;
